// LoginPage: gebruikersnaam + wachtwoord. Login loopt via AuthProvider
// (POST /api/auth/login).

import React, { useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Container,
    Stack,
    TextField,
    Typography,
    CircularProgress,
} from '@mui/material';
import { useAuth } from '@rvdh/react-auth-context';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

function translateError(err) {
    if (err?.statusCode === 401) { return 'Onjuiste gebruikersnaam of wachtwoord.'; }
    if (err?.statusCode === 429) { return err.message || 'Te veel loginpogingen. Probeer het later opnieuw.'; }
    if (err?.statusCode === 400) { return 'Vul je gebruikersnaam en wachtwoord in.'; }
    if (!err?.statusCode) { return 'Geen verbinding met de server. Controleer je internet en probeer opnieuw.'; }
    return 'Inloggen is mislukt. Probeer het opnieuw.';
}

export default function LoginPage() {
    const { login, isAuthenticated } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();

    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const redirectTo = location.state?.from?.pathname || '/';

    if (isAuthenticated && !loading) {
        return <Navigate to={redirectTo} replace />;
    }

    const handleSubmit = async (ev) => {
        ev.preventDefault();
        if (!username.trim() || !password) {
            setError('Vul je gebruikersnaam en wachtwoord in.');
            return;
        }
        setError(null);
        setLoading(true);
        try {
            await login(username, password);
            navigate(redirectTo, { replace: true });
        } catch (err) {
            setError(translateError(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'background.default',
                p: 2,
            }}
        >
            <Container maxWidth="xs">
                <Card variant="outlined">
                    <CardContent sx={{ p: 4 }}>
                        <Stack spacing={2} sx={{ mb: 3 }}>
                            <Typography variant="h1" sx={{ fontSize: '1.8rem' }}>
                                {t('app.title')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t('login.title')}
                            </Typography>
                        </Stack>

                        {error && (
                            <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
                        )}
                        <form onSubmit={handleSubmit} noValidate>
                            <Stack spacing={2}>
                                <TextField
                                    label="Gebruikersnaam"
                                    autoComplete="username"
                                    autoCapitalize="none"
                                    autoCorrect="off"
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    required
                                    autoFocus
                                />
                                <TextField
                                    label={t('login.password')}
                                    type="password"
                                    autoComplete="current-password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                />
                                <Button
                                    type="submit"
                                    variant="contained"
                                    size="large"
                                    disabled={loading}
                                    startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
                                >
                                    {t('login.submit')}
                                </Button>
                            </Stack>
                        </form>
                    </CardContent>
                </Card>
            </Container>
        </Box>
    );
}
